import { Link } from "react-router-dom";
import { Calculator, Gauge, ClipboardCheck, Fuel, Wrench, PiggyBank, TrendingUp, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PathMock, GarageMock, MiniFitMock, MiniPathMock, MiniGarageMock } from "@/components/mockups/AppMock";
import StoreBadges from "@/components/StoreBadges";
import { track } from "@/lib/analytics";

export function HowItWorks() {
  const steps = [
    { icon: Calculator, title: "Calculate", body: "Run the payment, total cost and real monthly numbers for any car you're considering." },
    { icon: Gauge, title: "See your Fit", body: "One clear score shows how the vehicle fits your income, debts and housing — the same number the app shows." },
    { icon: ClipboardCheck, title: "Make the plan", body: "In the app, see what's helping, what to watch, and the moves that bring the car within reach." },
  ];
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-16 bg-white border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.title} className="flex md:flex-col gap-4 items-start">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
                  <s.icon className="w-5 h-5" />
                </span>
                <span className="text-xs font-bold text-stone-300 md:hidden">Step {i + 1}</span>
              </div>
              <div>
                <h3 className="text-base font-bold text-stone-900 mb-1"><span className="hidden md:inline text-stone-300 font-bold mr-2">{i + 1}.</span>{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function PathToPurchase() {
  return (
    <section id="path-to-purchase" data-testid="path-to-purchase-section" className="py-20 bg-[#faf9f7] border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">App Feature Preview</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
              Not the Fit you wanted? Don't stop at "no."
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed mb-8">
              DriveClear can help you see what could move the vehicle closer to the Fit you want — a different down payment, a little less monthly debt, or a slightly different price.
            </p>
            <a href="#get-app" data-testid="path-cta" onClick={() => track("app_cta_clicked", { location: "path_to_purchase" })} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-white font-semibold text-sm transition-colors">
              Explore Path to Purchase in the App <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="flex justify-center"><PathMock /></div>
        </div>
      </div>
    </section>
  );
}

export function DreamGarage() {
  return (
    <section id="dream-garage" data-testid="dream-garage-section" className="py-20 bg-white border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center lg:order-1 order-2"><GarageMock /></div>
          <div className="order-1 lg:order-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">App Feature Preview</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
              Your dream car deserves a plan.
            </h2>
            <p className="text-stone-600 text-lg leading-relaxed mb-8">
              Save vehicles, set targets and watch your progress as your finances change. The Dream Garage keeps the car you want in sight — and shows you getting closer.
            </p>
            <a href="#get-app" data-testid="garage-cta" onClick={() => track("app_cta_clicked", { location: "dream_garage" })} className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-white font-semibold text-sm transition-colors">
              Build Your Dream Garage in DriveClear <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Ownership() {
  const cards = [
    { icon: Fuel, title: "Gas", body: "Track weekly fuel spending." },
    { icon: Wrench, title: "Maintenance", body: "Build toward a maintenance target." },
    { icon: PiggyBank, title: "Car Fund", body: "Track contributions toward an emergency vehicle fund." },
    { icon: TrendingUp, title: "Trade-In", body: "Follow estimated trade-in progress over time." },
  ];
  return (
    <section id="ownership" data-testid="ownership-section" className="py-20 bg-[#faf9f7] border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-2">App Feature Preview</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-3">DriveClear stays useful after you buy.</h2>
          <p className="text-stone-600 text-lg">Ownership doesn't end at purchase — and neither does DriveClear.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <div key={c.title} data-testid={`ownership-${c.title.toLowerCase().replace(" ", "-")}`} className="rounded-2xl bg-white border border-stone-200 p-5 shadow-sm">
              <span className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700 mb-3">
                <c.icon className="w-5 h-5" />
              </span>
              <h3 className="text-sm font-bold text-stone-900 mb-1">{c.title}</h3>
              <p className="text-xs text-stone-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-stone-400 mt-4">Refinance opportunity tracking is on the way in the app.</p>
      </div>
    </section>
  );
}

export function Discovery() {
  const cards = [
    { path: "/car-payment-calculator", title: "Car Payment Calculator", body: "Calculate an estimated monthly payment." },
    { path: "/car-affordability-calculator", title: "Car Affordability Calculator", body: "Estimate a comfortable vehicle-price range." },
    { path: "/dti-calculator", title: "DTI Calculator", body: "See how a car payment changes your debt-to-income ratio." },
    { path: "/car-cost-of-ownership", title: "Cost of Ownership", body: "Look beyond the payment to insurance, maintenance and fuel." },
    { path: "/car-loan-calculator", title: "Car Loan Calculator", body: "Explore principal, interest and total borrowing cost." },
  ];
  return (
    <section id="calculators" data-testid="discovery-section" className="py-20 bg-white border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-3">More ways to run the numbers.</h2>
          <p className="text-stone-600 text-lg">Free calculators, powered by the same DriveClear engine the app uses.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c) => (
            <Link key={c.path} to={c.path} data-testid={`discovery-${c.path.slice(1)}`} className="group rounded-2xl bg-[#faf9f7] border border-stone-200 p-6 hover:border-blue-300 hover:bg-white transition-colors">
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-blue-700 transition-colors">{c.title}</h3>
              <p className="text-sm text-stone-600 leading-relaxed mb-3">{c.body}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700">Open calculator <ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LoanRefi() {
  return (
    <section id="financing" data-testid="loan-refi-section" className="py-16 bg-[#faf9f7] border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-white border border-stone-200 p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-xl">
            <h2 className="text-2xl font-extrabold text-stone-900 tracking-tight mb-2">Know your numbers. Then explore your options.</h2>
            <p className="text-sm text-stone-600 leading-relaxed">
              Once you know your payment and your Fit, you can compare financing from a position of clarity. Partner loan and refinance options are coming soon.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button type="button" data-testid="loan-cta" onClick={() => track("loan_cta_clicked", { page: "home" })} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-stone-200 text-stone-800 font-semibold text-sm">
              Explore Auto Loan Options <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">Soon</span>
            </button>
            <button type="button" data-testid="refinance-cta" onClick={() => track("refinance_cta_clicked", { page: "home" })} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-stone-200 text-stone-800 font-semibold text-sm">
              Explore Refinance Options <span className="text-[10px] font-semibold uppercase tracking-wide text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">Soon</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section id="get-app" data-testid="final-cta-section" className="py-20 bg-white border-b border-stone-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
            The website helps you run the numbers.<br />DriveClear helps you make the plan.
          </h2>
          <p className="text-stone-600 text-lg">Fit Check, Path to Purchase, Dream Garage and ownership tracking — all in the app.</p>
        </div>
        <div className="flex flex-wrap justify-center items-end gap-6 mb-12">
          <MiniFitMock />
          <MiniPathMock />
          <MiniGarageMock />
        </div>
        <div className="flex justify-center">
          <StoreBadges dark testidPrefix="final" />
        </div>
      </div>
    </section>
  );
}

const FAQS = [
  {
    q: "Does using these calculators affect my credit score?",
    a: "No. Everything on this site is an educational estimate. There are no credit pulls, no accounts, and nothing is reported to credit bureaus.",
  },
  {
    q: "Is the Fit Score a loan approval?",
    a: "No. The DriveClear Fit is an educational estimate of how a vehicle fits your finances. It is not a credit offer, pre-approval, or lending decision — only lenders make those.",
  },
  {
    q: "Where do the numbers come from?",
    a: "Every calculation on this site is produced by the same DriveClear engine the mobile app uses, so the website and the app never disagree on the same inputs.",
  },
  {
    q: "Do you store my income or debts?",
    a: "No. Calculator and Fit inputs are used to compute your answer in the moment and are not stored. If you choose to continue in the app, you can explicitly hand your scenario over with a one-time code.",
  },
];

export function Faq() {
  return (
    <section id="faq" data-testid="faq-section" className="py-20 bg-[#faf9f7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight mb-8 text-center">Common questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="px-6 rounded-2xl bg-white border border-stone-200">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-base font-bold text-stone-900 hover:no-underline hover:text-blue-700 py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent data-testid={`faq-content-${i}`} className="text-sm text-stone-600 leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
